/**
 * bjjmap domain types.
 *
 * Source of truth: docs/DATA_MODEL.md.
 * If you change a shape here, update the matching JSON Schema in
 * /data/schema/*.json and the model doc, or downstream validation breaks.
 *
 * Convention: every position node is "what *the practitioner whose perspective
 * we're modeling* is currently in." Every Move is from-my-perspective. This is
 * why we have both `mount-top` and `mount-bottom` as separate nodes — they
 * offer wildly different option sets to the practitioner.
 */

export type Zone =
  | "back"
  | "back-bottom"
  | "back-top"
  | "clinch"
  | "guard"
  | "guard-bottom"
  | "guard-top"
  | "half-guard"
  | "half-guard-bottom"
  | "half-guard-top"
  | "mount"
  | "mount-bottom"
  | "mount-top"
  | "north-south-bottom"
  | "north-south-top"
  | "scrambled"
  | "side-control-bottom"
  | "side-control-top"
  | "standing"
  | "standing-bottom"
  | "standing-top"
  | "submitted"
  | "turtle-bottom"
  | "turtle-top"

export type Dominance = 'tori' | 'uke' | 'neutral';

export type Posture =
  | 'standing'
  | 'seated'
  | 'supine'
  | 'prone'
  | 'turtle'
  | 'inverted';

export type MoveCategory =
  | 'sweep'
  | 'submission'
  | 'pass'
  | 'escape'
  | 'takedown'
  | 'transition'
  | 'guard-retention';

// Sub-category for submissions. Allows clustering / filtering by the
// joint family targeted. Optional — only meaningful when category ===
// 'submission'. Leave undefined for non-submission moves.
export type SubmissionType =
  | 'choke'
  | 'joint-lock'
  | 'leg-lock'
  | 'neck-crank'
  | 'other';

// User-selectable attribute to cluster nodes by. 'none' is internal/legacy
// — it's no longer surfaced in the HUD but the type keeps it so the
// store doesn't have to validate.
export type ClusterBy =
  | 'none'
  | 'positionType'   // groups by WHERE the body is (top / bottom / neutral)
  | 'advantage'      // groups by WHO IS WINNING — deliberately not the same
  | 'posture'        // groups positions by body shape (supine, seated, …)
  | 'moveCategory'
  | 'submissionType'
  | 'disposition'
  | 'family'   // pulls variant nodes toward their family centroid
  | 'concept'; // pulls nodes toward shared cross-cutting principle

// High-level position category derived from a Position's zone. Used as
// the cluster key when ClusterBy === 'positionType'.
//
// This is GEOGRAPHY. It answers "where is the body", nothing else. The
// zone suffix used to answer that AND "who is winning" at the same time,
// which worked until the back family, where the player who has the back
// is winning decisively and is also underneath. `advantage` is the second
// axis those two questions were sharing.
export type PositionType = 'top' | 'bottom' | 'neutral' | 'terminal';

export type Gi = 'gi' | 'no-gi' | 'both';

/**
 * 4-tier difficulty label. Mapped from the legacy 1-5 integer scale as:
 *   1,2 -> 'fundamental', 3 -> 'intermediate', 4 -> 'advanced', 5 -> 'specialist'.
 * Filterable in the HUD and shown as its own column in the expanded drawer.
 */
export type Difficulty = 'fundamental' | 'intermediate' | 'advanced' | 'specialist';

/** Stable ordering for sort / iteration. Lower index = more accessible. */
export const DIFFICULTY_ORDER: Difficulty[] = [
  'fundamental',
  'intermediate',
  'advanced',
  'specialist',
];

export type Media = {
  kind: 'image' | 'video' | 'audio' | 'gif';
  url: string;
  caption?: string;
  credit?: string;
  durationSec?: number;
};

export type MoveStep = {
  title: string;
  body: string;
  mediaIdx?: number;
};

export type Position = {
  id: string;
  name: string;
  aliases?: string[];
  zone: Zone;
  dominance: Dominance;
  posture: Posture;
  /** Absent until the authoring pass runs; nodes fall back to their clips. */
  description?: string | null;
  prerequisites?: string[];
  notes?: string;
  media?: Media[];
  tags?: string[];
  /**
   * Optional override for the node's "narrative weight" in the graph,
   * range 0..1. When present, replaces the degree-derived ranking that
   * drives hub/bridge/orphan flags and shell radius. Leave unset to let
   * the topology speak for itself.
   */
  importance?: number;
  /**
   * Filterable difficulty. Same scale as Move.difficulty. Defaults to
   * 'fundamental' if absent (assumed accessible).
   */
  difficulty?: Difficulty;
  /**
   * Variant family — every member of a `family` is a positional variant
   * of the same underlying idea (e.g. all open-guard variations share
   * `family: 'open-guard'`). The HUD's collapse-variants toggle hides
   * non-canonical members. Force-sim cluster strength is bumped within
   * a family when the user clusters by 'family'.
   */
  family?: string;
  /** True for the canonical / representative member of the family. */
  isCanonical?: boolean;
  /**
   * Cross-cutting BJJ principles this entity exemplifies (kuzushi,
   * destabilization, gripfight, dilemma, etc.). Soft-tagged — a filter
   * chip, not a node in the graph. Drawn from outlier's `concept`-class
   * tags.
   */
  concepts?: string[];
};

export type Move = {
  id: string;
  name: string;
  aliases?: string[];
  from: string;
  to: string;
  category: MoveCategory;
  /** Absent until the authoring pass runs; nodes fall back to their clips. */
  description?: string | null;
  /** True when the technique returns you to the position it began in — a
   *  grip break, a wrist snap. Lets the validator accept the self-loop. */
  noPositionalChange?: boolean;
  steps: MoveStep[];
  media?: Media[];
  difficulty?: Difficulty;
  gi?: Gi;
  counters?: string[];
  popularity?: number; // 0..1
  tags?: string[];
  importance?: number; // 0..1, optional narrative-weight override
  submissionType?: SubmissionType; // optional sub-category; only meaningful when category === 'submission'
  /**
   * Variant family — moves that are stylistic siblings of each other
   * (e.g. all heel-hook variants share `family: 'heel-hook'`). Mirrors
   * Position.family.
   */
  family?: string;
  /** True for the canonical / representative member of the family. */
  isCanonical?: boolean;
  /** Cross-cutting principles. Mirrors Position.concepts. */
  concepts?: string[];
  /** Free-form curation/provenance note. Mirrors Position.notes. */
  notes?: string;
};

/**
 * A Counter is a Move performed in response to an opponent's attack.
 * Structurally a counter IS a move: it has all the same descriptive fields
 * (category, difficulty, gi, family, concepts, aliases, tags, etc.). The
 * only counter-specific additions are:
 *  - `intercepts`: the Move id this counter is responding to. The graph
 *    renderer uses this to place an interception marker on the parent
 *    move's arc midpoint instead of drawing a separate node + edges.
 *  - `result`: where the counter executor ends up if it lands. Mirrors
 *    Move.to — both fields hold the same Position id on a counter (the
 *    `to` field exists so a counter is structurally interchangeable with
 *    a move everywhere a Move is consumed; `result` is the canonical
 *    name).
 *  - `probability`: how often the counter actually lands when attempted.
 *
 * The counter's `from` is the same as the intercepted move's `from`
 * (that's the position the action is unfolding from). `category` is the
 * action category of the counter itself (a stack pass against an armbar
 * is `pass`; a hitchhiker escape from mount armbar is `escape`).
 */
export type Counter = Move & {
  intercepts: string;
  result: string;
  probability?: number;
};

// ---------- Derived / runtime types (not persisted) ----------

export type NodeKind = 'position' | 'move' | 'interception';

export type GraphNode = {
  id: string;
  kind: NodeKind;
  label: string;
  zone?: Zone;
  degree: number;
  /** [0,1] effective narrative weight — importance override or normalized degree. */
  weight: number;
  bridgeScore: number;
  isHub: boolean;
  isBridge: boolean;
  isOrphan: boolean;
  // sim-driven; updated by the worker every tick
  x?: number;
  y?: number;
  z?: number;
  // Interception-only: lets the renderer sample the parent move's bezier arc
  // so the interception node sits on its midpoint instead of having its own
  // force-sim position.
  parentMoveId?: string;
  bezierT?: number;
  /**
   * Where this node's degree sits in the whole graph's distribution, 0-1.
   *
   * Computed in build-graph because that is the only place that has every
   * node's degree at once. The point is that it is RELATIVE: the busiest
   * node is at 1 whether it has 69 connections or 205, so a size curve
   * built on this does not silently rescale every time the dataset grows.
   */
  degreePercentile?: number;
  /**
   * log1p(degree) / log1p(maxDegree), so 0-1 with the busiest node at 1.
   *
   * Unlike `degreePercentile` this preserves the SPACING between degrees
   * rather than only their order — a node with 200 connections stays far
   * from one with 8 — while still being pinned at the top, so the size
   * curve built on it does not drift as the graph grows.
   */
  degreeNorm?: number;
};

/**
 * What a node's size should mean.
 *
 * `degree` maps the raw connection count through a log curve. It was tuned
 * when the busiest node had 69 connections; `standing` now has 205, so the
 * top of the range has drifted well past where it was designed to sit.
 *
 * `rank` maps the node's PERCENTILE instead, which is scale-free — the
 * busiest node lands in the same place no matter how big the graph gets.
 *
 * `uniform` removes size from the picture entirely, which is the honest
 * way to look at structure when you do not want the eye dragged to hubs.
 */
export type SizeBy = 'degree' | 'rank' | 'uniform';

export type GraphEdge = {
  id: string;
  source: string;
  target: string;
  kind: 'move' | 'counter-fork' | 'intended-continuation' | 'inversion-chain';
  /**
   * Default/representative move id for this edge.
   *  - For a move-edge sleeve: equals moveIds[0] (the default move).
   *  - For a counter-fork edge: the parent move being intercepted.
   */
  moveId?: string;
  /**
   * ±1 when this edge has a reverse counterpart (A→B and B→A both
   * exist), 0 otherwise. The two directions bow opposite ways so they
   * render as separate, separately-clickable arcs instead of overlapping
   * on one curve. See subArcControl() in graph/bezier.ts.
   */
  dirSign?: number;
  /**
   * For position-mode move edges: the full list of moves that share the
   * same (source, target). Length 1 = a "singleton sleeve" (just one
   * move between the two positions). Length > 1 = a true sleeve that
   * collapses into one arc by default and fans out on hover.
   * Undefined for non-move edges (counter-fork, inversion-chain).
   */
  moveIds?: string[];
  counterId?: string;
  weight: number;
};

export type Graph = {
  nodes: GraphNode[];
  edges: GraphEdge[];
  byId: Map<string, GraphNode>;
  adjacency: Map<string, string[]>; // node id → neighbor node ids
  zoneCentroid: Partial<Record<Zone, [number, number, number]>>;
};

// ---------- Top-level config ----------

export type AppConfig = {
  centerNodeId: string;
  centerNodeFallback?: { position?: string | null; move?: string | null };
  hubCount?: number;
  labelCount?: number;
};

// ---------- DataSource adapter (for future DB providers) ----------

export interface DataSource {
  positions(): Promise<Position[]>;
  moves(): Promise<Move[]>;
  counters(): Promise<Counter[]>;
  config(): Promise<AppConfig>;
  mediaRefs(): Promise<MediaRefsIndex | null>;
}

// ---------- Media references (sourced from outlierdb scrape) ----------

export type MediaRef = {
  youtubeId: string;
  startSec: number;
  embedUrl: string;
  watchUrl: string;
  note: string;
  hashtags: string[];
  isResource: boolean;
  sourcedFrom: 'outlierdb' | string;
  matchScore: number;
};

export type MediaRefsIndex = {
  schemaVersion: number;
  generatedAt: string;
  sourceProvenance: Record<string, string>;
  /** Keyed by Position.id. */
  positions: Record<string, MediaRef[]>;
  /** Keyed by Move.id. */
  moves: Record<string, MediaRef[]>;
  /** Keyed by Counter.id. Counters are structurally moves but tracked separately so the
   *  upstream scrape job can match them by their counter-specific identity. */
  counters?: Record<string, MediaRef[]>;
};
